import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import apiService from "../ApiService";
import { baseUrl } from "../config";
import { trackEvent } from "../components/trackingUtils";

const SeoMetaChecker = () => {
  const [url, setUrl] = useState("");
  const [seoData, setSeoData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seoScore, setScore] = useState(0);
  const [scoredetails, setScoreDetails] = useState();

  useEffect(() => {
    trackEvent("PageLoad", "", "SEOMetaChecker");
  }, []);

  const handleFetchSeoData = async () => {
    setLoading(true);
    try {
      const response = await apiService.fetchSEOData(url); // Assuming apiService is correctly implemented
      trackEvent("Click", url, response.status);
      if (response.status === "success") {
        setSeoData(response.audit_report);
        setScore(response.audit_report.seo_score);
        setScoreDetails(response.score_details);
        setError(null);
      } else {
        setError("Failed to fetch SEO data");
        setSeoData(null);
      }
    } catch (error) {
      setError("Failed to fetch SEO data");
      setSeoData(null);
    } finally {
      setLoading(false);
    }
  };

  // Function to determine the color based on score
  const getScoreColor = () => {
    const score = seoScore;
    if (score >= 20) return "bg-green-500"; // High score
    if (score >= 10) return "bg-yellow-500"; // Medium score
    return "bg-red-500"; // Low score
  };

  const title = "SEO Navigator - Free Website Analysis & Insights | GeniusNexa";
  const description =
    "Perform a comprehensive SEO audit with our free SEO Navigator. Identify website issues, optimize content, and improve rankings. Start your SEO analysis now.";
  const keywords =
    "SEO Navigator, free SEO analysis, website SEO audit, SEO optimization, improve SEO rankings, SEO tool, SEO checker, website analysis tool";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={`${baseUrl}/images/seonavigator.png`}
        />
        <meta property="og:url" content={`${baseUrl}/tools/seonavigator`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
            <div
              className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center p-3 bg-[#E5C558]/20 rounded-2xl mb-6 backdrop-blur-sm">
                <svg
                  className="w-10 h-10 text-[#E5C558]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  SEO
                </span>
                <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
                  Navigator
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Comprehensive SEO audit and analysis tool. Optimize your
                website's performance with detailed insights and actionable
                recommendations.
              </p>

              {/* Feature Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {[
                  {
                    icon: "🔍",
                    title: "Deep Analysis",
                    desc: "Complete SEO audit",
                  },
                  {
                    icon: "📊",
                    title: "SEO Score",
                    desc: "Performance rating",
                  },
                  {
                    icon: "🎯",
                    title: "Meta Check",
                    desc: "Title & description",
                  },
                  {
                    icon: "🚀",
                    title: "Quick Scan",
                    desc: "Instant results",
                  },
                ].map((feature, idx) => (
                  <div
                    key={idx}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10"
                  >
                    <div className="text-2xl mb-2">{feature.icon}</div>
                    <h3 className="text-white font-semibold text-sm mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 text-xs">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Section - Light */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* URL Input Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 mb-8 border border-gray-200 shadow-xl">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                  <div className="p-2 bg-[#E5C558]/20 rounded-xl">
                    <svg
                      className="w-6 h-6 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path
                        fillRule="evenodd"
                        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  Website SEO Analysis
                </h2>
                <p className="text-gray-600">
                  Enter your website URL to get a comprehensive SEO audit
                </p>
              </div>

              {/* SEO Score Display */}
              {seoData && (
                <div
                  className={`p-6 rounded-xl text-white mb-6 ${getScoreColor()}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold">
                        SEO Score: {seoScore}
                      </h3>
                      <p className="text-sm opacity-90">
                        Based on SEO best practices
                      </p>
                    </div>
                    <div className="text-4xl font-bold opacity-75">
                      {seoScore >= 20 ? "🎯" : seoScore >= 10 ? "⚠️" : "❌"}
                    </div>
                  </div>
                </div>
              )}

              {/* URL Input */}
              <div className="flex gap-3 mb-6">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    id="urlInput"
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.example.com"
                    aria-label="Enter URL"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                    <svg
                      className="w-5 h-5 text-gray-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={handleFetchSeoData}
                  disabled={!url || loading}
                  className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:from-[#d4b54d] hover:to-[#E5C558] text-black font-semibold px-8 py-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-[#E5C558]/25"
                >
                  {loading ? (
                    <>
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Analyze SEO
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error}
                </div>
              )}
            </div>

            {/* SEO Results */}
            {seoData && (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] p-6 rounded-xl text-white">
                  <h3 className="text-xl font-bold mb-2">📊 SEO Score</h3>
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl font-bold">
                      {Math.round(
                        (scoredetails.title_length_score +
                          scoredetails.meta_description_present_score +
                          scoredetails.num_internal_links_score) /
                          3
                      )}
                    </div>
                    <div className="text-sm opacity-90">
                      Overall SEO Performance
                    </div>
                  </div>
                </div>

                {/* Title Analysis */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                        🏷️ Page Title
                      </h3>
                      <p className="text-gray-600 mt-2 leading-relaxed">
                        {seoData.title || "No title found"}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-white text-sm font-medium ${
                        scoredetails.title_length_score >= 10
                          ? "bg-green-500"
                          : scoredetails.title_length_score >= 5
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                    >
                      {scoredetails.title_length_score}/10
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Length: {seoData.title ? seoData.title.length : 0}{" "}
                    characters
                  </div>
                </div>

                {/* Meta Description */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                        📝 Meta Description
                      </h3>
                      <p className="text-gray-600 mt-2 leading-relaxed">
                        {seoData.meta_description ||
                          "No meta description found"}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-white text-sm font-medium ${
                        scoredetails.meta_description_present_score >= 10
                          ? "bg-green-500"
                          : scoredetails.meta_description_present_score >= 5
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                    >
                      {scoredetails.meta_description_present_score}/10
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Length:{" "}
                    {seoData.meta_description
                      ? seoData.meta_description.length
                      : 0}{" "}
                    characters
                  </div>
                </div>

                {/* Meta Keywords */}
                {seoData.meta_keywords && seoData.meta_keywords.length > 0 && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      🏷️ Meta Keywords
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {seoData.meta_keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Open Graph */}
                {seoData.og_properties && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      📱 Open Graph Properties
                    </h3>
                    <div className="space-y-4">
                      {seoData.og_properties.og_title && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Title:
                          </span>
                          <p className="text-gray-600 mt-1">
                            {seoData.og_properties.og_title}
                          </p>
                        </div>
                      )}
                      {seoData.og_properties.og_image && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Image:
                          </span>
                          <img
                            src={seoData.og_properties.og_image}
                            alt="Open Graph"
                            className="mt-2 max-w-xs rounded-lg shadow-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Links Analysis */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Internal Links */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                        🔗 Internal Links
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-white text-sm font-medium ${
                          scoredetails.num_internal_links_score >= 10
                            ? "bg-green-500"
                            : scoredetails.num_internal_links_score >= 5
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                      >
                        {scoredetails.num_internal_links_score}/10
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      Found{" "}
                      {seoData.internal_links
                        ? seoData.internal_links.length
                        : 0}{" "}
                      internal links
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {seoData.internal_links &&
                        seoData.internal_links.map((link, index) => (
                          <a
                            key={index}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-blue-600 hover:text-blue-800 text-sm truncate"
                          >
                            {link}
                          </a>
                        ))}
                    </div>
                  </div>

                  {/* External Links */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      🌐 External Links
                    </h3>
                    <div className="text-sm text-gray-600 mb-3">
                      Found{" "}
                      {seoData.external_links
                        ? seoData.external_links.length
                        : 0}{" "}
                      external links
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {seoData.external_links &&
                        seoData.external_links.map((link, index) => (
                          <a
                            key={index}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-blue-600 hover:text-blue-800 text-sm truncate"
                          >
                            {link}
                          </a>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#E5C558]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-[#E5C558] animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                </div>
                <p className="text-gray-700 text-lg font-medium">
                  🔍 Analyzing SEO...
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  Scanning your website for SEO insights
                </p>
              </div>
            )}

            {/* No Data State */}
            {!loading && !seoData && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-gray-700 text-lg font-medium">
                  🚀 Ready for SEO Analysis
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  Enter a valid URL and click 'Analyze SEO' to get started
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SeoMetaChecker;
